import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { library } from "@common/library/library";
import { store } from "@common/store/store";
import { configStore } from "@common/configStore";
import { ddStoreFilter } from "@common/ddStore";
import { formatControlLogic } from "@common/control/controlStore";
import { actionStore } from "@common/actionStore";
import { recordStore } from "@common/recordStore";

export const controlMixins = {
    data() {
        return {
            baseOptions: [], // options must be stored in an original reference array
            valueChangeTimeout: false,
            updateBehaviorActions: [
                { text: 'Add - checked values', value: 'add' },
                { text: 'Remove - checked values', value: 'remove' },
                { text: 'Replace - all values', value: 'replace' }
            ],
            interpolatedOptions: [],
            interpolatingOptions: false,
            controlPendingSubscriptions: []
        };
    },
    computed: {
        recordStore() {
            return recordStore();
        },
        controlPsp() {
            return this?.component && this?.component?.recordPsp ? this.component.recordPsp : false;
        },
        value: {
            get() {
                if (!this?.config) return null;
                if (this.config.storePropValueSync) {
                    return this.$store.state[this.component.parentStoreId][this.config.storePropValueSync];
                }
                this.validateValue(this.config.value, true);
                return this.config.value;
            },
            set(value) {
                if (!this?.config) return;
                if (this.config.storePropValueSync) {
                    this.$store.state[this.component.parentStoreId][this.config.storePropValueSync] = value;
                }
                this.config.value = value;
                this.$parent.$emit('update:value', value);
                if (this.component && typeof this.component.onUpdate === 'function') this.component.onUpdate();
                this.validateValue(this.config.value);
            }
        },
        configStore() {
            return configStore.state.cfg;
        },
        library() {
            return library;
        },
        dataTestId() {
            return this.$parent.dataTestId;
        },
        loading() {
            return this.$parent.loading;
        },
        loadingOptions() {
            return this.$parent.loadingOptions;
        },
        controlLoading() {
            return this.$parent.controlLoading;
        },
        component() {
            if (this.$parent.component) return this.$parent.component;
            // If this.$parent.component doesn't exist
            // Set component to dialog or other dynamic template "fromDynamicTemplate"
            if (this.$parent?.$parent?.$parent.fromDynamicTemplate) return this.$parent.$parent.$parent;
            return undefined;
        },
        controls() {
            return this.component.controls;
        },
        config() {
            return this.$parent.config;
        },
        syncfusionConfig() {
            let tmpConfig = library.cloneObj(this.config);
            // value should be removed from config because:
            // v-bind convert all config to properties, so ejs-datepicker has :value and v-model properties
            // this behavior cause issues with date component
            delete tmpConfig.value;
            delete tmpConfig.index;
            delete tmpConfig.sortOrder;
            delete tmpConfig.valid;
            delete tmpConfig.validationErrors;
            if (this.config.ENABLE_HTML || this.config.enableHtml) delete tmpConfig.cssClass;
            if (this.config.type === 'dropdown') delete tmpConfig.text;
            return tmpConfig;
        },
        name() {
            return this.$parent.name;
        },
        cId() {
            return this.$parent.cId;
        },
        controlStoreIndex() {
            return this.$parent.controlStoreIndex;
        },
        doMissingOptionsCheck() {
            return (typeof VUE_PROJECT_NAME === 'undefined' || (VUE_PROJECT_NAME !== 'undefined' && VUE_PROJECT_NAME !== 'signin' && VUE_PROJECT_NAME !== 'coordinator'))
                && (this.config.type === 'dropdown' || this.config.type === 'dropdowntree' || this.config.type === 'multiselect')
                && (this.config.type !== 'multiselect' || !this.config.allowCustomValue)
                && (!this.config.notShowMissingOptions)
                && this.value
                && this.value.length;
        },
        optionsReady() {
            return !this.loadingOptions && !this.interpolatingOptions && !this.controlPendingSubscriptions.length && this.configOptions;
        },
        configOptions() {
            let config = this.config;
            let outputItems = library.cloneObj(this.$parent.recordStoreItems || this.config.items);
            let originKey = 0;
            if (typeof outputItems !== 'object' && !this.config.additionalOptions) {
                return [];
            }
            if (!Array.isArray(outputItems)) {
                let itemsAsArray = [];
                for (let i in outputItems) {
                    if (i === 'length') continue;
                    itemsAsArray.push(outputItems[i]);
                }
                outputItems = itemsAsArray;
            }
            if (Array.isArray(this.config.additionalOptions)) {
                outputItems = [...outputItems, ...this.config.additionalOptions];
            }
            outputItems = outputItems.map((item, idx) => {
                if (typeof item === 'object') {
                    // Try to remap properties to config.fields props
                    let textKey = (config.fields && config.fields.text) || 'text';
                    let valueKey = (config.fields && config.fields.value) || 'value';
                    if (typeof item[textKey] === 'undefined') item[textKey] = item.text || item.label || item.value;
                    if (typeof item[valueKey] === 'undefined') item[valueKey] = item.value || item.text || item.label;
                    // dropdownbutton does not support config.fields so map values to item.text and item.value
                    if (config.type === 'dropdownbutton') {
                        item.text = item[textKey];
                        item.value = item[valueKey];
                    }
                    if (typeof item[textKey] !== 'string') item[textKey] += "";
                    else if (config.type === 'dropdowntree' && typeof item.expanded === 'undefined') {
                        item.expanded = false;
                    }
                    if (typeof item.key === 'undefined') item.key = idx;
                    item.originKey = originKey;
                    originKey++;
                } else if (typeof item === 'string' && config.fields) {
                    let textKey = config.fields.text;
                    let valueKey = config.fields.value;
                    let itemValue = item;
                    item = {};
                    if (textKey) item[textKey] = itemValue;
                    if (valueKey) item[valueKey] = itemValue;
                }
                return item;
            });
            // Filter Items (including parent options)
            let itemsFilter = config.itemsFilter;
            if (itemsFilter) {
                let parentOptionIds = [];
                let addParentItems = () => {
                    let parentOptions = this.config.items.filter((item) => item.hasChild && parentOptionIds.indexOf(item.value) !== -1);
                    outputItems = outputItems.concat(parentOptions);
                };
                itemsFilter = library.getRealIndexes(itemsFilter, this, this.$parent.itemIndexArray);
                if (itemsFilter.indexOf('__index__') === -1) {
                    outputItems = outputItems.filter((item) => {
                        this.item = item;
                        let include = library.eval.call(this, itemsFilter);
                        if (include && item.pId && parentOptionIds.indexOf(item.pId) === -1) parentOptionIds.push(item.pId);
                        return include;
                    });
                    if (typeof outputItems === 'undefined') { // NOSONAR
                        outputItems = [];
                    }
                    if (config.type === 'dropdowntree') addParentItems();
                }
            }
            // Sort remaining items
            let itemsSort = config.itemsSort;
            if (Array.isArray(outputItems) && itemsSort && itemsSort.sortBy) {
                outputItems = library.sortObject(outputItems, itemsSort.sortBy, itemsSort.direction);
            }
            let tmpOutputItems = library.cloneObj(outputItems);
            for (let i in tmpOutputItems) {
                if (outputItems && outputItems[i] && typeof outputItems[i].absoluteOptionPosition !== 'undefined') {
                    let tmpPosition = outputItems[i].absoluteOptionPosition;
                    if (tmpPosition < 0) tmpPosition = 0;
                    if (tmpPosition >= outputItems.length) tmpPosition = outputItems.length - 1;
                    outputItems = library.moveArrayItem(outputItems, i, tmpPosition);
                }
            }
            if (!library.isEqualObj(this.baseOptions, outputItems)) {
                this.baseOptions = outputItems;

                if (
                    this.useInterpolatedOptions
                    && this.baseOptions.length
                ) {
                    this.interpolatingOptions = true;
                    this.$nextTick(() => {
                        let optText = this.config.fields && this.config.fields.text;
                        library.interpolateOptions.call(this, this.baseOptions, optText, this.config.itemTemplate).then((interpolatedOptions) => {
                            if (!library.isEqualObj(this.interpolatedOptions, interpolatedOptions)) {
                                this.interpolatedOptions = interpolatedOptions;
                            }
                            this.$nextTick(() => {
                                this.interpolatingOptions = false;
                            });
                        });
                    });
                }
            }
            return this.baseOptions;
        },
        useInterpolatedOptions() {
            return (
                this.config.type === "dropdown"
                || this.config.type === "multiselect"
                || this.config.type === "dropdowntree"
            )
            && this.config.itemTemplate
            && this.config.useInterpolatedOptions;
        },
        options() {
            let outputItems = this.useInterpolatedOptions ? this.interpolatedOptions : this.configOptions;
            if (this.doMissingOptionsCheck) {
                let getMissingOptions = (values, options) => {
                    let output = [];
                    let optKey = this.config.fields && this.config.fields.value;
                    let optText = this.config.fields && this.config.fields.text;
                    let nestedChildKey = this.config.fields && this.config.fields.child;
                    let tmpValue = Array.isArray(values) ? values : [values];
                    for (let i in tmpValue) {
                        if (i === 'length') continue;
                        let item;
                        item = options.find((iOption) => {
                            let iOptVal = optKey ? iOption[optKey] : iOption;
                            if (iOptVal === tmpValue[i]) return iOption;
                            if (typeof iOptVal === 'number' && iOptVal === parseFloat(tmpValue[i])) return iOption;
                            // Also check nested child options
                            let innerItem;
                            if (nestedChildKey && Array.isArray(iOption[nestedChildKey])) {
                                innerItem = iOption[nestedChildKey].find((iiOption) => {
                                    let iiOptVal = optKey ? iiOption[optKey] : iiOption;
                                    if (iiOptVal === tmpValue[i]) return iiOption;
                                    if (typeof iiOptVal === 'number' && iiOptVal === parseFloat(tmpValue[i])) return iiOption;
                                    return false;
                                });
                            }
                            if (innerItem) return innerItem;
                            return false;
                        });
                        if (!item) {
                            if (optKey) {
                                let newOption = {};
                                newOption[optKey] = tmpValue[i];
                                newOption[optText] = '(missing value): ' + tmpValue[i];
                                newOption.isMissing = true;
                                output.push(newOption);
                            }
                            else {
                                output.push('(missing value): ' + tmpValue[i]);
                            }
                        }
                    }
                    return output;
                };
                let missingOptions = getMissingOptions(this.value, outputItems);
                if (Array.isArray(missingOptions) && missingOptions.length) {
                    let dropdownRef = this.$refs[this.name + '_' + this.cId];
                    if (dropdownRef) {
                        missingOptions.forEach((option) => {
                            outputItems.push(option);
                            if (this.config.type === 'dropdown') dropdownRef.addItem(option);
                            else if (typeof dropdownRef?.ej2Instance?.treeItems === 'object') {
                                dropdownRef.ej2Instance.treeItems.push(option);
                            }
                        });
                    }
                }
            }
            return this.ddStoreFilter(outputItems);
        },
        missingOptions() {
            return this.options.filter((option) => option.isMissing);
        },
        valuesMap() {
            return this.$parent.valuesMap;
        },
        asField() {
            return this.$parent.asField || this.config.asField;
        },
        recordId() {
            return this.$parent.recordId || this.config.recordId;
        },
        ddLoading() {
            return store.state.ddStore.loading;
        },
        saveBehaviorCtrl() {
            if (!this.config?.saveBehaviorCtrl) return false;
            const value = library.findPathAndValue(this?.component?.paramValues, 'setBehavior')?.value;
            if (value) this.setUpdateBehavior(value);
            return true;
        }
    },
    methods: {
        setupValidationRules() {
            let rules = [];
            this.config.valid = true;
            this.config.validationErrors = [];
            this.config.cssClass = this.config.cssClass.replace(' e-error', '');
            if (this.config.required) {
                rules.push(function (v, _this) {
                    if (typeof v === 'string') v = v.trim();
                    if (v || (_this.config.type === 'boolean' && typeof v !== 'undefined')) return false;
                    return _this.config.label + " is required";
                });
            }
            if (this.config.type === 'date' && (this.config.maxDate || this.config.minDate)) {
                // Process min and max date evaluations even if the field is not required
                let moment = library.getMomentObj();
                if (this.config.maxDate) {
                    rules.push((v) => (v && this.config.maxDate && moment(v).isAfter(this.config.maxDate)
                        ? this.config.label + ' Date is after the maximum date'
                        : false
                    ));
                }
                if (this.config.minDate) {
                    rules.push((v) => (v && this.config.minDate && moment(v).isBefore(this.config.minDate)
                        ? this.config.label + ' Date is before the minimum date'
                        : false
                    ));
                }
            }
            this.config.rules = rules;
        },
        async validateValue(value, fromTimeout) {
            // debounce validation checking
            if (!fromTimeout) {
                clearTimeout(this.valueChangeTimeout);
                this.valueChangeTimeout = setTimeout(() => {
                    this.valueChangeTimeout = 0;
                    this.validateValue(value, true);
                }, 600); // NOSONAR
                return;
            }
            if (this.config) {
                if (typeof this.onValidateValue === 'function') { // TODO do we need this?
                    // local control validation
                    this.onValidateValue();
                    return;
                }
                let iRules = this.config.rules;
                let validationErrors = [];
                let valid = true;
                if (!this.config.disabled) {
                    for (let i in iRules) {
                        let ruleError = iRules[i](value, this);
                        if (!ruleError) continue; // Continue to next rule if there is no validation error
                        validationErrors.push(ruleError);
                        valid = false;
                    }
                }
                if (this.config.valid !== valid) {
                    this.config.valid = valid;
                    this.config.validationErrors = validationErrors;
                    if (!valid) this.config.cssClass += ' e-error';
                    else this.config.cssClass = this.config.cssClass.replace(' e-error', '');
                }
            }
        },
        controlValue(controlName) {
            return this.controls[controlName].value;
        },
        ddStoreFilter(value, cId) {
            if (!cId && this.component && this.component.cId) cId = this.component.cId;
            if (this.ddLoading && !ddStoreFilter(value, cId)) {
                return '';
            }
            return ddStoreFilter(value, cId);
        },
        /* for history
        checkDataDictionary(iControl) {
            let flattenControl = library.flatten(iControl);
            let useDDPaths = Object.keys(flattenControl).filter((key) => typeof flattenControl[key] === 'string' && flattenControl[key].indexOf('DD.') !== -1 && key.indexOf('controls') === -1);
            // debugger;
            if (useDDPaths.length) {
                for (let i in useDDPaths) {
                    let stringToTranslate = library.getByPath(iControl, useDDPaths[i], '');
                    const regexp = /DD\.([^ .]+)\.([^ .]+)/gm;
                    const matches = stringToTranslate.matchAll(regexp);
                    for (const match of matches) {
                        let group = match[1];
                        let labelId = match[2];
                        if (ddStore.state.availableGroups.indexOf(group) !== -1) {
                            // eslint-disable-next-line no-unused-expressions
                            ddStore.state.records.control[group][labelId];
                        }
                    }
                }
            }
            return iControl;
        },
        */
        click(event) {
            this.action(event, 'onclick');
        },
        async action(event, actionType) {
            let methodName;
            let actionParams;
            let actionArgs;
            let eventMethods = this.config.eventMethods;
            if (typeof eventMethods === 'undefined' && this.config.name.indexOf('__') !== -1) {
                // eventMethods are not copied when control created through the master control MASTER_CONTROL_NAME[__]TMP_CONTROL_NAME
                let tmpRealCtrlName = this.config.name.split('__')[0];
                if (this.component && this.component.controls && this.component.controls[tmpRealCtrlName] && this.component.controls[tmpRealCtrlName].eventMethods) {
                    eventMethods = this.component.controls[tmpRealCtrlName].eventMethods;
                }
            }
            let method = eventMethods && eventMethods[actionType] ? eventMethods[actionType] : false;
            let isChainingActions = false;
            if (typeof method === 'object' && !Array.isArray(method)) {
                if (method.action === 'layoutProperty' || method.action === 'layoutProperty.') methodName = 'layoutProperty.';
                else methodName = 'actionStore.' + method.action;
                // Process record sources
                // let payload = library.recordSourceMapper(method.action, method.params, this.component.record, this.component);
                // actionParams = payload;
                // actionArgs = payload;
            } else if (Array.isArray(method) && method.length && method[0].action) {
                isChainingActions = true;
            } else methodName = method;
            let realMethodName = methodName;
            let realActionType = actionType;
            let setArgs = () => {
                if (actionArgs) return actionArgs; // set by library.recordSourceMapper
                let args = [];
                for (let i in eventMethods[realActionType + 'Args']) {
                    if (i === 'length') continue;
                    if (eventMethods[realActionType + 'Args'][i].arg === 'this') {
                        args.push(this.$parent.component);
                    } else {
                        let argValue = library.getByPath(this.$parent.component, eventMethods[realActionType + 'Args'][i].arg);
                        if (!argValue) {
                            argValue = eventMethods[realActionType + 'Args'][i].arg;
                        }
                        args.push(argValue);
                    }
                }
                args = { ...args };
                return args;
            };
            let setLayoutProp = () => {
                if (this.$parent.component && this.$parent.component.parentStoreId && store.state[this.$parent.component.parentStoreId]) {
                    let setNewVal = (iActionArgs, storeVal) => {
                        let type = iActionArgs.type;
                        if (type === 'numberOffset') return storeVal + iActionArgs.offset;
                        if (type === 'toggleBoolean') return !storeVal;
                        if (type === 'controlValue') return event.value;
                        return iActionArgs.val;
                    };
                    if (actionArgs && !library.objIsEmpty(actionArgs)) {
                        this.$store.state[this.$parent.component.parentStoreId][actionArgs.prop] = setNewVal(actionArgs, store.state[this.$parent.component.parentStoreId][actionArgs.prop]);
                    }
                    else {
                        for (let i in eventMethods[realActionType + 'Args']) {
                            if (i === 'length') continue;
                            let val = eventMethods[realActionType + 'Args'][i].value || event.value;
                            this.$store.state[this.$parent.component.parentStoreId][eventMethods[realActionType + 'Args'][i].arg] = val;
                        }
                    }
                }
            };
            if (!actionParams) actionParams = this.$parent && this.$parent.$attrs && typeof this.$parent.$attrs['actionParams'] !== 'undefined' ? this.$parent.$attrs['actionParams'] : false;
            if (!methodName) {
                // TODO determine if we can delete below line, it is causing actionType like ononclick
                // actionType = 'on' + actionType;
                realActionType = actionType;
                methodName = eventMethods && eventMethods[actionType] ? eventMethods[actionType] : false;
            }
            // Execute method
            // Check for method on component
            // OR check for method in actionStore
            // OR check for setting layoutProperty
            this.config.processing = true;
            let response;
            if (isChainingActions) {
                response = await actionStore.runChainedActions(method, { ...this.component.record, ...{ controlValue: this.value } }, this.component);
            } else if (methodName && this.$parent.component && typeof this.$parent.component[methodName] === 'function') {
                let controlConfig = library.cloneObj(this.config);
                if (typeof this.$parent.index !== 'undefined') {
                    controlConfig.index = this.$parent.index;
                }
                // additional data can be passed as attributes of the controls
                for (let i in this.$parent.$attrs) {
                    if (typeof controlConfig[i] === 'undefined') {
                        controlConfig[i] = this.$parent.$attrs[i];
                    }
                }
                response = await this.$parent.component[methodName](event, controlConfig, actionParams);
            } else if (typeof methodName === 'function') {
                let controlConfig = library.cloneObj(this.config);
                if (typeof this.$parent.index !== 'undefined') {
                    controlConfig.index = this.$parent.index;
                }
                // additional data can be passed as attributes of the controls
                for (let i in this.$parent.$attrs) {
                    if (typeof controlConfig[i] === 'undefined') {
                        controlConfig[i] = this.$parent.$attrs[i];
                    }
                }
                response = await methodName(event, controlConfig, actionParams, this);
            } else if (realMethodName && realMethodName.indexOf('actionStore.') !== -1) {
                let actionStoreMethod = realMethodName.replace('actionStore.', '');
                if (actionStore && typeof actionStore[actionStoreMethod] === 'function') {
                    let payload = library.recordSourceMapper(method.action, method.params, this.component.record, this.component, true);
                    // Note: payload here is only params without source
                    response = await store.dispatch('actionStore/' + actionStoreMethod, { payload }, { root: true });
                }
            } else if (realMethodName && realMethodName.indexOf('layoutProperty.') !== -1) {
                response = await setLayoutProp();
            } else if (methodName && this.$parent.$parent && this.$parent.$parent[methodName]) {
                response = await this.$parent.$parent[methodName](event, actionParams);
            } else if (methodName && this.$parent.$parent && this.$parent.$parent.$parent && this.$parent.$parent.$parent[methodName]) {
                response = await this.$parent.$parent.$parent[methodName](event, actionParams);
            }
            this.config.processing = false;
            return response;
        },
        async runSecondaryAction(event, index) {
            if (typeof index !== 'undefined' && event && typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
            if (!this.config.secondaryActions || !this.config.secondaryActions.length) return;
            // if no event.item then main action button is clicked
            if (!index) index = 0;
            let currAction = event.item || this.config.secondaryActions[index];
            let responseObj = {
                control: this.config,
                responseObj: {
                    responses: []
                }
            };
            let callAction = async (action, index) => {
                let response = 'failure';
                if (!action.params) action.params = {};
                action.params.commonParams = {
                    config: this.config,
                    value: this.value
                };
                if (!currAction.doNotIncludeComponent) {
                    action.params.commonParams.component = this.component;
                }
                let payload = library.recordSourceMapper(action.action, library.cloneObj(action.params), this.config, this.component);
                if (action.source === 'actionStore') response = await actionStore[action.action](payload);
                else if (action.source === 'component' && this.component) {
                    // TODO check for the action on the local component
                    // Instead of this one statically configured method
                    response = this.component.setSelectedComponent(responseObj);
                }
                responseObj.responseObj.responses[index] = response;
                return response;
            };
            // TODO move this chain actions code into the actionStore
            if (currAction.chain) {
                // Chain through all actions
                let promises = [];
                let actionChain = [currAction, ...currAction.chain];
                actionChain.forEach((iAction, index) => {
                    promises.push(new Promise((resolve) => {
                        if (index > 0) {
                            let prevPromise = promises[index - 1];
                            prevPromise.then(() => {
                                callAction(iAction, index).then((response) => {
                                    resolve(response);
                                });
                            });
                        } else {
                            callAction(iAction, index).then((response) => {
                                resolve(response);
                            });
                        }
                    }));
                });
                await library.promiseAllSettled(promises);
            }
            else {
                let actionResult = new Promise((resolve) => {
                    callAction(currAction, 0).then((response) => {
                        resolve(response);
                    });
                });
                await actionResult;
            }
            if (currAction.runLocalAfterAction) {
                if (typeof this[currAction.runLocalAfterAction] === 'function') {
                    this[currAction.runLocalAfterAction]();
                }
                if (typeof this.$parent[currAction.runLocalAfterAction] === 'function') {
                    this.$parent[currAction.runLocalAfterAction]();
                }
            }
        },
        mapRefsToComponent() {
            // TODO figure out of we need this
            for (let i in this.$refs) {
                if (i === 'length') continue;
                if (this.$refs[i] && this.$parent.component && typeof this.$parent.component.refs === 'object' && typeof this.$parent.component.refs[i] === 'undefined') {
                    this.$parent.component.refs[i] = this.$refs[i].ej2Instances;
                    if (this.config && this.config.type === 'logic' && !this.$refs[i].ej2Instances) {
                        this.$parent.component.refs[i] = this.$refs[i];
                    }
                }
            }
        },
        setUpdateBehavior(args) {
            const value = args?.item?.value || args;
            let path = this.config.configSource
                .replace('this.', '')
                .replace('__index__', this.config.index)
                // remove last path and replace it with .setBehavior
                .replace(/\.[^.]+$/, `.setBehavior`);

            library.setByPath(this, path, value);

            this.updateBehaviorActions.forEach((action) => {
                const isSelected = action.value === value;
                action.selected = isSelected;
                action.iconCss = isSelected ? 'fa-solid fa-check' : false;
            });

            this.updateBehaviorActions = [...this.updateBehaviorActions];
        }
    },
    watch: {
        // config.logic path watchers are added in the mounted() method
    },
    updated() {
        this.mapRefsToComponent();
    },
    mounted() {
        this.mapRefsToComponent();
        if (this.config && this.config.logic) {
            // Let all controls in the view to be created before running logic watchers
            let formattedLogic = formatControlLogic(this.config, this);
            let logicKeys = library.getLogicPropsToWatch(formattedLogic, { object2: 'component.record' });
            if (Array.isArray(logicKeys)) {
                logicKeys.forEach((lKey) => {
                    lKey = lKey
                        .replace('this.', '')
                        .replace("['", ".")
                        .replace("']", "");
                    this.$watch(lKey, () => {
                        let record = this.component && this.component.record;
                        library.logicParser.call(this, formattedLogic, this.config, record);
                    }, { immediate: true });
                });
            }
        }
        if (this.config) {
            this.setupValidationRules();
        }
    },
    components: {
        labelWrapper: defineAsyncComponent(() => import('@common/control/labelWrapper')),
        displayValue: defineAsyncComponent(() => import('@common/control/displayValue')),
        'ejs-button': defineAsyncComponent(() => import('@common/syncfusion/button')),
        'ejs-dropdownbutton': defineAsyncComponent(() => import('@common/syncfusion/dropdownButton'))
    }
};
